import { useEffect } from "react";
import { useUser } from "@/features/user";
import { useTerms } from "@/features/terms/hooks/useTerms";
import { useError } from "@/providers/Error";
import { cnpMessages, genericMessages } from "@/constants/errorMessages";
import { isCnpValid } from "@/lib/cnpUtils";
import { useCnpInUseModal } from "../components/CnpInUseModal";
import { useOnboarding } from "./useOnboarding";

export { CNP_LENGTH } from "@/lib/cnpUtils";

interface UseSubmitCnpReturn {
  // Resolves true once the CNP is saved and the onboarding state refreshed.
  submitCnp: (cnp: string) => Promise<boolean>;
  isLoading: boolean;
  cnpInUseModal: React.ReactNode;
}

/**
 * Saves the CNP the way the CNP page and the chat CNP step both need it:
 * format check, CNP already used by another account, and the BC consent
 * asked next to the CNP in the preliminary offer flows.
 */
export const useSubmitCnp = ({
  acceptsBcConsent,
  onError,
}: {
  acceptsBcConsent: boolean;
  onError?: (message: string) => void;
}): UseSubmitCnpReturn => {
  const { setError } = useError();
  // The chat answers errors in the conversation instead of the popover.
  const reportError = onError ?? setError;
  const { getOnboardingState } = useOnboarding();
  const cnpInUseModal = useCnpInUseModal();
  const { acceptBcTerms, isLoading: isAcceptingBcTerms } = useTerms();
  const {
    updatePersonalData,
    isUpdatePending: isUpdatePersonalDataPending,
    updatePersonalDataError,
  } = useUser();

  useEffect(() => {
    if (updatePersonalDataError) {
      reportError(genericMessages.unexpected);
    }
  }, [updatePersonalDataError]);

  const submitCnp = async (cnp: string) => {
    // Caught here, before the backend rejects it the same way.
    if (!isCnpValid(cnp)) {
      reportError(cnpMessages.invalid_format);
      return false;
    }

    let result;
    try {
      result = await updatePersonalData({ cnp });
    } catch {
      // Reported by the mutation error effect.
      return false;
    }

    if (!result.isSuccess && result.cnpError && result.cnpMaskedPhone) {
      cnpInUseModal.open(result.cnpMaskedPhone);
      return false;
    }

    if (!result.isSuccess) {
      reportError(cnpMessages.invalid_cnp);
      return false;
    }

    // A failure here is not blocking: the scoring step asks for the consent
    // again on its own screen.
    if (acceptsBcConsent) {
      await acceptBcTerms().catch(() => undefined);
    }

    await getOnboardingState();
    return true;
  };

  return {
    submitCnp,
    isLoading: isUpdatePersonalDataPending || isAcceptingBcTerms,
    cnpInUseModal: cnpInUseModal.modal,
  };
};
